import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { checkAccessLevel } from '../../lib/authenticate';
import { checkShiftForRendering } from './dashboardUtils';

interface WeekViewProps {
    daysOfTheCurrentWeek: moment.Moment[];
    filteredShifts: any[];
    assignments: any[];
    positions: any[];
    holidays: any[];
    availabilityMode: boolean;
    isDarkMode: boolean;
    selectedSchedule: any;
    accessLevel: string | null;
    getAvailabilityColor: (isDarkMode: boolean, date: string) => string;
    shouldShowDay: (date: moment.Moment, isHoliday: boolean, isSubmitNext: boolean) => boolean;
    onShiftClick: (shift: any, dayDate: moment.Moment) => void;
    onAddShiftClick: (date: moment.Moment) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
    daysOfTheCurrentWeek,
    filteredShifts,
    assignments,
    positions,
    holidays,
    availabilityMode,
    isDarkMode,
    selectedSchedule,
    accessLevel,
    getAvailabilityColor,
    shouldShowDay,
    onShiftClick,
    onAddShiftClick,
}) => {
    return (
        <Box sx={{ p: 0, pt: 0 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    height: { xs: '400px', sm: '500px', md: '600px' },
                    gap: 0,
                    minHeight: 0,
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                {daysOfTheCurrentWeek.map((dayOfTheCurrentWeek: moment.Moment, dayIndex: number) => {
                    let holidayDay = null;
                    let employeeCuttoffDate = selectedSchedule?.activeScheduleDate;
                    let submitNextScheduleDate =
                        dayOfTheCurrentWeek.format('YYYY-MM-DD') ===
                        moment(selectedSchedule?.activeScheduleDate).subtract(0, 'day').format('YYYY-MM-DD');

                    holidays.forEach((holiday: any) => {
                        if (moment(holiday.date).format('YYYY-MM-DD') === dayOfTheCurrentWeek.format('YYYY-MM-DD')) {
                            holidayDay = holiday.name;
                        }
                    });

                    let availabilityColor = '';
                    if (availabilityMode) {
                        availabilityColor = getAvailabilityColor(isDarkMode, dayOfTheCurrentWeek.format('YYYY-MM-DD'));
                    }
                    if (dayOfTheCurrentWeek < moment().subtract(2, 'day')) {
                        availabilityColor += 'd4';
                    }

                    return (
                        <Box
                            key={dayIndex}
                            sx={{
                                borderRight: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                minWidth: '100%',
                                maxWidth: '100%',
                                flexDirection: 'column',
                                bgcolor: submitNextScheduleDate
                                  ? selectedSchedule?.color+"6e"
                                  : holidayDay
                                  ? isDarkMode
                                    ? 'rgba(106, 195, 255, 0.25)'
                                    : '#ffdfc5ff'
                                  : 'transparent',
                            }}
                        >
                            <Box
                                sx={{
                                    p: { xs: 0.5, sm: 1, md: 1.5 },
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    textAlign: 'center',
                                    minHeight: { xs: 40, sm: 50, md: 60 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption" fontWeight={700}>
                                    {dayOfTheCurrentWeek.format('ddd')}
                                </Typography>
                                {dayOfTheCurrentWeek.isSame(moment(), 'day') ?
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color={'white'}
                                        borderRadius={"100%"}
                                        sx={{ bgcolor: 'primary.main', width: { xs: 18, sm: 22, md: 24 }, height: { xs: 18, sm: 22, md: 24 }, lineHeight: '24px', mx: 'auto' }}
                                    >
                                        {dayOfTheCurrentWeek.format('D')}
                                    </Typography>
                                    : <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color={'text.primary'}
                                    >
                                        {dayOfTheCurrentWeek.format('D')}
                                    </Typography>}

                                {holidayDay && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', 
                                    display: { xs: 'none', sm: 'block' }, textOverflow: 'ellipsis', overflow: 'hidden', wordOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {holidayDay}
                                    </Typography>
                                )}
                            </Box>

                            {shouldShowDay(dayOfTheCurrentWeek, !!holidayDay, submitNextScheduleDate) && (
                                <Box
                                    onClick={(e) => {
                                        if (checkAccessLevel(accessLevel)) {
                                            const isShiftClick = (e.target as HTMLElement).closest('.shift-pill');
                                            if (!isShiftClick) {
                                                onAddShiftClick(dayOfTheCurrentWeek);
                                            }
                                        }
                                    }}
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: { xs: 0.5, sm: 0.75, md: 1 },
                                        textOverflow: 'ellipsis', overflow: 'hidden', wordOverflow: 'ellipsis',
                                        minHeight: 0,
                                        p: { xs: 0.5, sm: 0.75, md: 1 },
                                        bgcolor: availabilityColor || 'transparent',
                                        cursor: checkAccessLevel(accessLevel) ? 'pointer' : 'default',
                                    }}
                                >
                                    {filteredShifts.map((shift: any, idx: number) => {
                                        const context = {
                                            shift,
                                            idx,
                                            dayDate: dayOfTheCurrentWeek,
                                            assignments,
                                            positions,
                                            filteredShifts,
                                            accessLevel,
                                            employeeCuttoffDate,
                                        };

                                        const { shouldRender, color, assignedStaffCount } = checkShiftForRendering(context);

                                        if (!shouldRender) return null;

                                        return (
                                            <Box
                                                key={idx}
                                                className="shift-pill"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onShiftClick(shift, dayOfTheCurrentWeek);
                                                }}
                                                sx={{
                                                    px: { xs: 0.5, sm: 0.75 },
                                                    py: { xs: 0.125, sm: 0.25 },
                                                    borderRadius: 0.5,
                                                    bgcolor:
                                                        employeeCuttoffDate < dayOfTheCurrentWeek.format('YYYY-MM-DD')
                                                            ? `divider`
                                                            : `${color}.light`,
                                                    border: 1,
                                                    borderColor:
                                                        employeeCuttoffDate < dayOfTheCurrentWeek.format('YYYY-MM-DD')
                                                            ? `text.primary`
                                                            : `${color}.main`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: { xs: 0.25, sm: 0.5 },
                                                    minHeight: { xs: 14, sm: 16, md: 18 },
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor:
                                                            employeeCuttoffDate < dayOfTheCurrentWeek.format('YYYY-MM-DD')
                                                                ? `divider`
                                                                : `${color}.main`,
                                                        transform: 'scale(1.03)',
                                                        boxShadow: 1,
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                                                        fontWeight: 600,
                                                        color:
                                                            employeeCuttoffDate < dayOfTheCurrentWeek.format('YYYY-MM-DD')
                                                                ? `text.primary`
                                                                : `background.paper`,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        flex: 1,
                                                        lineHeight: 1.2,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: { xs: 5, sm: 4, md: 3 },
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {shift.shiftName}
                                                </Typography>
                                                {checkAccessLevel(accessLevel) && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                                                            color:
                                                                employeeCuttoffDate < dayOfTheCurrentWeek.format('YYYY-MM-DD')
                                                                    ? `text.primary`
                                                                    : `background.paper`,
                                                            fontWeight: 600,
                                                            flexShrink: 0,
                                                            display: { xs: 'none', sm: 'none', md: 'flex' },
                                                        }}
                                                    >
                                                        {assignedStaffCount}/{shift.staffNeeded}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default WeekView;
